// @flow

import _ from 'lodash';
import React from 'react';
import Accordion from 'client/components/accordion/accordion';
import EmbeddedLinkHandler from 'client/components/util/embeddedLinkHandler';
import LinkButton from 'client/components/button/linkButton';
import Loader from 'client/components/loader';
import Panel from 'client/components/accordion/panel';
import PanelList from 'client/components/list/panelList/list';
import PanelListItem from 'client/components/list/panelList/item';
import SmsPhoneInput from 'client/components/form/smsPhoneInput';
import StrideHeaderWrapper from 'client/components/strideHeaderWrapper';
import Translate from 'client/components/translate';

import type { LocationType } from 'types/react-router.flow';
import type { UserType } from 'web-core/types/user';
import { Tracking } from 'web-core/src/tracking';
import { extendTaxSettings } from 'routes/tax/components/benefits/utils';
import {
  DEDUCTION_GUIDE,
  FILING_DISCOUNT,
  STRIDE_TAX,
  SUPPORT,
  TAX_GUIDE,
  TRACKING_PREFIX,
} from './constants';

const BENEFIT_LIST_PREFIX = 'taxes.benefits.taxBenefits';
const SMS_KEY = 'drive-app-link';
const panelIds = ['hrb', 'partnerGuide', 'deductionGuide', 'support', 'taxApp'].reduce(
  (obj, key) => ({ ...obj, [key]: key }),
  {},
);
const panelIdToTrackingNameMap = {
  [panelIds.hrb]: FILING_DISCOUNT,
  [panelIds.partnerGuide]: TAX_GUIDE,
  [panelIds.deductionGuide]: DEDUCTION_GUIDE,
  [panelIds.support]: SUPPORT,
  [panelIds.taxApp]: STRIDE_TAX,
};

const TRACKING_NAME_EXPANDED = `${TRACKING_PREFIX} Expanded`;
const TRACKING_NAME_SELECTED = `${TRACKING_PREFIX} Selected`;

const Hero = () => (
  <div className="hero pattern flex-column">
    <h3 className="white-color">
      <Translate path="taxes.benefits.payoff.hero.header" />
    </h3>
    <h5 className="white-color">
      <Translate path="taxes.benefits.payoff.hero.subheader" />
    </h5>
  </div>
);

type Props = {
  brand: string,
  allBenefits: Array<Object>,
  user: UserType,
  location: LocationType,
  saveUser: Function,
  fetchConfiguration: Function,
  setSelectedBenefits: Function,
  hostText: Object,
  translations: Object,
  isSuperHost: boolean,
  taxConfig: Object,
  selectedBenefits: Array<Object>,
};

type State = {
  isLoading: boolean,
};

class PayoffPage extends React.Component<Props, State> {
  state = {
    isLoading: true,
  };

  componentDidMount() {
    // two scenarios:
    // user was logged out and selected some number of benefits
    // they will be redirected from the account create page with query string params
    // so pull those selected keys and save to the user
    // OR user is logged in and comes straight from the selection page
    // the store will have the current selected benefits keys,
    // but so will the query string, so still pull from there
    const { user, location } = this.props;
    // Airbnb sends Superhosts to the site with sh=1 query param
    const isSuperHost = _.get(location, 'query.sh') === '1';
    let taxBenefits = _.get(location, 'query.selected', []);

    if (_.isString(taxBenefits)) {
      taxBenefits = [taxBenefits];
    }
    const requests = [this.props.fetchConfiguration()];

    if (isSuperHost || taxBenefits.length) {
      const settings = extendTaxSettings({ user, taxBenefits, isSuperHost });

      if (!_.isEqual(settings, user.settings)) {
        requests.push(
          this.props.saveUser({ settings }),
          this.props.setSelectedBenefits(taxBenefits),
        );
      }
    }

    return Promise.all(requests).then(() =>
      this.setState({
        isLoading: false,
      }));
  }

  togglePanel = (state) => {
    const panelId = _.get(state, 'panel.props.id');
    const trackingName = panelIdToTrackingNameMap[panelId];
    Tracking.track(TRACKING_NAME_EXPANDED, { Choice: trackingName });
  };

  renderHrbPanel = () => {
    const { hostText, translations } = this.props;
    return (
      <Panel
        key={panelIds.hrb}
        id={panelIds.hrb}
        title={<Translate path={`${BENEFIT_LIST_PREFIX}.hrb.title`} using={hostText} />}
        illustration={_.get(translations, `${BENEFIT_LIST_PREFIX}.hrb.illustration`)}>
        <div>
          <div>
            <p>
              <Translate path={`${BENEFIT_LIST_PREFIX}.hrb.content.instructions`} />
            </p>
          </div>
          <LinkButton
            className="cta-button auto"
            target="_blank"
            onClick={() =>
              Tracking.track(TRACKING_NAME_SELECTED, { Choice: 'Filing Discount Site' })
            }
            href={_.get(translations, `${BENEFIT_LIST_PREFIX}.hrb.content.ctaUrl`)}>
            <Translate path={`${BENEFIT_LIST_PREFIX}.hrb.content.cta`} using={hostText} />
          </LinkButton>
        </div>
      </Panel>
    );
  };

  renderPartnerGuidePanel = () => {
    const { hostText, translations } = this.props;
    return (
      <Panel
        key={panelIds.partnerGuide}
        id={panelIds.partnerGuide}
        title={<Translate path={`${BENEFIT_LIST_PREFIX}.partnerGuide.title`} using={hostText} />}
        illustration={_.get(translations, `${BENEFIT_LIST_PREFIX}.partnerGuide.illustration`)}>
        <div>
          <p>
            <Translate
              path={`${BENEFIT_LIST_PREFIX}.partnerGuide.content.description`}
              using={hostText} />
          </p>
          <LinkButton
            className="cta-button auto"
            target="_blank"
            onClick={() => Tracking.track(TRACKING_NAME_SELECTED, { Choice: TAX_GUIDE })}
            href={_.get(translations, `${BENEFIT_LIST_PREFIX}.partnerGuide.content.ctaUrl`)}>
            <Translate path={`${BENEFIT_LIST_PREFIX}.partnerGuide.content.cta`} using={hostText} />
          </LinkButton>
        </div>
      </Panel>
    );
  };

  renderDeductionGuidePanel = () => {
    const { hostText, translations } = this.props;
    return (
      <Panel
        key={panelIds.deductionGuide}
        id={panelIds.deductionGuide}
        title={<Translate path={`${BENEFIT_LIST_PREFIX}.deductionGuide.title`} using={hostText} />}
        illustration={_.get(translations, `${BENEFIT_LIST_PREFIX}.deductionGuide.illustration`)}>
        <div>
          <p>
            <Translate
              path={`${BENEFIT_LIST_PREFIX}.deductionGuide.content.description`}
              using={hostText} />
          </p>
          <LinkButton
            className="cta-button auto"
            target="_blank"
            onClick={() => Tracking.track(TRACKING_NAME_SELECTED, { Choice: DEDUCTION_GUIDE })}
            href={_.get(translations, `${BENEFIT_LIST_PREFIX}.deductionGuide.content.ctaUrl`)}>
            <Translate
              path={`${BENEFIT_LIST_PREFIX}.deductionGuide.content.cta`}
              using={hostText} />
          </LinkButton>
        </div>
      </Panel>
    );
  };

  renderSupportPanel = () => {
    const { hostText, isSuperHost, taxConfig, translations } = this.props;
    return (
      <Panel
        key={panelIds.support}
        id={panelIds.support}
        title={<Translate path={`${BENEFIT_LIST_PREFIX}.support.title`} using={hostText} />}
        illustration={_.get(translations, `${BENEFIT_LIST_PREFIX}.support.illustration`)}>
        <div>
          <EmbeddedLinkHandler
            clickHandler={() =>
              Tracking.track(TRACKING_NAME_SELECTED, { Choice: 'Support Guide' })
            }>
            <p>
              <Translate
                path={`${BENEFIT_LIST_PREFIX}.support.content.description`}
                using={hostText} />
            </p>
          </EmbeddedLinkHandler>
          <LinkButton
            className="cta-button auto"
            onClick={() => Tracking.track(TRACKING_NAME_SELECTED, { Choice: 'Support Email' })}
            href={_.get(translations, `${BENEFIT_LIST_PREFIX}.support.content.ctaUrl`)}>
            <Translate path={`${BENEFIT_LIST_PREFIX}.support.content.cta`} using={hostText} />
          </LinkButton>
          {taxConfig.showPhone && isSuperHost && (
            <EmbeddedLinkHandler
              clickHandler={() =>
                Tracking.track(TRACKING_NAME_SELECTED, { Choice: 'Support Phone' })
              }>
              <p className="pad-bottom">
                <Translate path={`${BENEFIT_LIST_PREFIX}.support.content.phone`} using={hostText} />
              </p>
            </EmbeddedLinkHandler>
          )}
        </div>
      </Panel>
    );
  };

  renderTaxAppPanel = () => {
    const { brand, hostText, translations } = this.props;
    return (
      <Panel
        key={panelIds.taxApp}
        id={panelIds.taxApp}
        title={<Translate path={`${BENEFIT_LIST_PREFIX}.taxApp.title`} using={hostText} />}
        illustration={_.get(translations, `${BENEFIT_LIST_PREFIX}.taxApp.illustration`)}>
        <div>
          <p>
            <Translate
              path={`${BENEFIT_LIST_PREFIX}.taxApp.content.description`}
              using={hostText} />
          </p>
          <div className="hide-for-medium">
            <LinkButton
              href="https://stride.app.link/g4wEYJhAHB"
              className="cta-button auto"
              target="_blank"
              onClick={() =>
                Tracking.track(TRACKING_NAME_SELECTED, { Choice: 'Stride Tax Download' })
              }>
              <Translate path={`${BENEFIT_LIST_PREFIX}.taxApp.content.download`} using={hostText} />
            </LinkButton>
          </div>
          <div className="show-for-medium">
            <SmsPhoneInput
              source={`${_.capitalize(brand)} Tax Rewards`}
              smsKey={SMS_KEY}
              smsUrl="drive/sms" />
          </div>
        </div>
      </Panel>
    );
  };

  renderSelectedBenefits() {
    const { selectedBenefits } = this.props;
    return (
      <Accordion onChange={this.togglePanel}>
        {selectedBenefits.map(b =>
          (_.isFunction(this.keyToComponentMap[b.id]) ? this.keyToComponentMap[b.id]() : null))}
      </Accordion>
    );
  }

  renderUnselectedBenefits() {
    const { allBenefits, selectedBenefits } = this.props;
    const selectedBenefitKeys = selectedBenefits.map(b => b.id);
    const unselectedBenefits = allBenefits.filter(
      benefit => selectedBenefitKeys.indexOf(benefit.id) === -1,
    );
    return unselectedBenefits.length ? (
      <div>
        <h3>
          <Translate path="taxes.benefits.unselectedBenefitsTitle" />
        </h3>
        <Accordion onChange={this.togglePanel}>
          {unselectedBenefits.map(b =>
            (_.isFunction(this.keyToComponentMap[b.id]) ? this.keyToComponentMap[b.id]() : null))}
        </Accordion>
      </div>
    ) : null;
  }

  renderOtherBenefits() {
    const { translations } = this.props;
    const listItems = _.get(translations, 'taxes.benefits.otherBenefits', []);

    if (!listItems.length) {
      return null;
    }

    return (
      <div className="pad-bottom-2">
        <h3>
          <Translate path="taxes.benefits.otherBenefitsTitle" />
        </h3>
        <PanelList>
          {listItems.map(item => (
            <PanelListItem
              {...item}
              key={item.id}
              target="_blank"
              clickHandler={() => {
                Tracking.track(TRACKING_NAME_SELECTED, { Source: _.capitalize(item.id) });
              }} />
          ))}
        </PanelList>
      </div>
    );
  }

  keyToComponentMap = {
    hrb: this.renderHrbPanel,
    partnerGuide: this.renderPartnerGuidePanel,
    deductionGuide: this.renderDeductionGuidePanel,
    support: this.renderSupportPanel,
    taxApp: this.renderTaxAppPanel,
  };

  render() {
    return (
      <StrideHeaderWrapper
        showPhone={false}
        noShadow
        activeLink="actionCenter"
        logoHref="/tax/rewards"
        navItems={['about', 'actionCenter', 'dashboardTax', 'account']}>
        <div>
          <Hero />
          {this.state.isLoading ? (
            <div className="text-center">
              <h4 className="margin-2">
                <Translate path="common.loading.update" />
              </h4>
              <Loader />
            </div>
          ) : (
            <div className="hero-intruder">
              {this.renderSelectedBenefits()}
              {this.renderUnselectedBenefits()}
              {this.renderOtherBenefits()}
            </div>
          )}
        </div>
      </StrideHeaderWrapper>
    );
  }
}

export default PayoffPage;
